import logToSlackMutation from "../mutations/LogToSlack.graphql";
import React from "react";
import { CircularProgress, Grid, Typography } from "@material-ui/core";
import { ArrowForward } from "@material-ui/icons";

import client from "../apollo";
import {
  isChunkLoadError,
  shouldSuppressChunkLoadSlackLog,
} from "../utils/chunkLoadErrors";
import CustomButton from "./CustomButton";


const LEGACY_CHUNK_RETRY_COUNTER_KEY = "chunkLoadErrorRetryCounter";
const CHUNK_RETRY_STATE_KEY = "chunkLoadErrorRetryState";
const CHUNK_RETRY_WINDOW_MS = 2 * 60 * 1000;

const clearChunkRetryState = () => {
  try {
    sessionStorage.removeItem(LEGACY_CHUNK_RETRY_COUNTER_KEY);
    sessionStorage.removeItem(CHUNK_RETRY_STATE_KEY);
  } catch (storageError) {
    // Ignore session storage errors.
  }
};

const readChunkRetryState = () => {
  try {
    const rawState = sessionStorage.getItem(CHUNK_RETRY_STATE_KEY);
    if (!rawState) {
      return { count: 0, updatedAt: 0 };
    }

    const parsedState = JSON.parse(rawState);
    return {
      count: parsedState?.count || 0,
      updatedAt: parsedState?.updatedAt || 0,
    };
  } catch (storageError) {
    clearChunkRetryState();
    return { count: 0, updatedAt: 0 };
  }
};

const readActiveChunkRetryState = () => {
  const chunkRetryState = readChunkRetryState();

  if (
    chunkRetryState.updatedAt &&
    Date.now() - chunkRetryState.updatedAt > CHUNK_RETRY_WINDOW_MS
  ) {
    clearChunkRetryState();
    return { count: 0, updatedAt: 0 };
  }

  return chunkRetryState;
};

const registerChunkRetryAttempt = () => {
  const chunkRetryState = readActiveChunkRetryState();
  const nextState = {
    count: chunkRetryState.count + 1,
    updatedAt: Date.now(),
  };

  sessionStorage.setItem(CHUNK_RETRY_STATE_KEY, JSON.stringify(nextState));

  return nextState.count;
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      showErrorMessage: false,
    };
    this.hasLoggedError = false;
    this.errorMessageTimer = null;
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      showErrorMessage: false,
    };
  }

  componentWillUnmount() {
    if (this.errorMessageTimer) {
      window.clearTimeout(this.errorMessageTimer);
    }
  }

  shouldRetryChunkReload(error) {
    if (!isChunkLoadError(error)) {
      return false;
    }

    try {
      const chunkRetryState = readActiveChunkRetryState();
      if (chunkRetryState.count < 1) {
        registerChunkRetryAttempt();
        return true;
      }
    } catch (storageError) {
      return false;
    }

    return false;
  }

  componentDidCatch(error, errorInfo) {
    if (this.shouldRetryChunkReload(error)) {
      window.location.reload();
      return;
    }

    if (this.errorMessageTimer) {
      window.clearTimeout(this.errorMessageTimer);
    }
    this.errorMessageTimer = window.setTimeout(() => {
      this.setState({ showErrorMessage: true });
    }, 1000);

    if (this.hasLoggedError) {
      return;
    }
    this.hasLoggedError = true;

    const pathname =
      typeof window !== "undefined" ? window.location.pathname : "unknown";
    const href =
      typeof window !== "undefined" ? window.location.href : "unknown";
    const userAgent =
      typeof window !== "undefined" ? window.navigator.userAgent : "unknown";

    if (shouldSuppressChunkLoadSlackLog(error, userAgent)) {
      return;
    }

    client
      .mutate({
        mutation: logToSlackMutation,
        variables: {
          input: {
            title:
              "Erro de frontend capturado no ErrorBoundary (energea.com.br)",
            type: "platform-error",
            data: [
              {
                label: "Mensagem",
                value: error?.message || "Erro sem mensagem",
              },
              {
                label: "Stack",
                value: error?.stack || "Sem stack",
              },
              {
                label: "Component Stack",
                value: errorInfo?.componentStack || "Sem component stack",
              },
              {
                label: "Pathname",
                value: pathname,
              },
              {
                label: "URL",
                value: href,
              },
              {
                label: "User Agent",
                value: userAgent,
              },
            ],
          },
        },
      })
      .catch(() => {});
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.resetKey !== prevProps.resetKey &&
      prevState.hasError &&
      this.state.hasError
    ) {
      if (this.errorMessageTimer) {
        window.clearTimeout(this.errorMessageTimer);
      }
      this.hasLoggedError = false;
      this.setState({
        hasError: false,
        error: null,
        showErrorMessage: false,
      });
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Grid
        container
        style={{ height: "100dvh", padding: "1rem" }}
        alignItems="center"
        justifyContent="center"
      >
        {this.state.showErrorMessage ? (
          <Grid item style={{ textAlign: "center" }}>
            <Typography gutterBottom>
              Desculpe! O conteúdo desta página foi atualizado recentemente.
            </Typography>
            <CustomButton
              variant="contained"
              color="primary"
              size="large"
              pill
              style={{ marginTop: "1rem" }}
              onClick={() => {
                clearChunkRetryState();
                window.location.reload();
              }}
              endIcon={<ArrowForward />}
            >
              Clique aqui para continuar
            </CustomButton>
          </Grid>
        ) : (
          <CircularProgress color="primary" />
        )}
      </Grid>
    );
  }
}

export default AppErrorBoundary;
